import React, { useEffect, useState } from 'react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

import DeleteIcon from 'assets/img/delete.png';
import { useProductStocks } from 'hooks/useProductStocks';
import { CartBodyRequest } from 'providers/CartProvider';
import { MOBILE } from 'settings/mediaQuery';

import QuantityField from '../../OrderByReference/QuantityField/QuantityField';
import ReferenceField from '../../OrderByReference/ReferenceField/ReferenceField';
import SizeField from '../../OrderByReference/SizeField/SizeField';

import styles from './Reference.module.scss';
import { StockVariante } from 'types/StocksReference';

function Reference({
  row,
  updateReference,
}: {
  row: number;
  updateReference: (key: number, reference: CartBodyRequest | null) => void;
}): JSX.Element | null {
  const [errorMsg, setErrorMsg] = useState('');
  const isMobile = useMediaQuery({
    query: MOBILE,
  });
  const [quantity, setQuantity] = useState<string>('1');
  const [reference, setReference] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<
    StockVariante | undefined
  >(undefined);
  const { t } = useTranslation();

  const {
    error,
    isValidReference,
    getProduct,
    getAvailableQuantities,
    stocks,
    stocksVariante,
  } = useProductStocks(reference);
  const product = getProduct(selectedVariant?.REFREEL || '');
  const availableQuantities = getAvailableQuantities();

  function handleDelete() {
    setReference('');
    setSelectedVariant(undefined);
  }

  useEffect(() => {
    if ((stocks && !isValidReference) || error) {
      setErrorMsg(t('LABEL_PRODUCT_NOT_FOUND'));
    } else {
      setErrorMsg('');
    }
  }, [isValidReference, reference, stocks, error]);

  useEffect(() => {
    if (selectedVariant?.REFREEL) {
      const updatedReference = {
        catalogSku: reference,
        sku: selectedVariant.REFREEL,
        qty: quantity,
        sizeLabel: product?.TAILLE,
        url: '',
        price: selectedVariant?.PRIX,
        preDiscountPrice: selectedVariant?.PRIX_BARRE,
        stock: selectedVariant?.ETAT_STOCK,
      };
      updateReference(row, updatedReference);
    } else {
      updateReference(row, null);
    }
  }, [quantity, reference, selectedVariant]);

  if (isMobile) {
    return (
      <div className={styles.container}>
        <div className={styles.referenceField}>
          <ReferenceField
            errorMsg={errorMsg}
            hasStock={!!stocks}
            isValidReference={isValidReference}
            mediaMobile
            reference={reference}
            setReference={setReference}
          />
          <button
            className={styles.deleteButton}
            type="button"
            onClick={handleDelete}
          >
            <img src={DeleteIcon} alt="remove" className={styles.deleteIcon} />
            <div className={styles.deleteLabel}>{t('LABEL_DELETE')}</div>
          </button>
        </div>
        <div>
          <SizeField
            disableField={errorMsg == t('LABEL_PRODUCT_NOT_FOUND')}
            isValidReference={isValidReference}
            mediaMobile
            reference={reference}
            setQuantity={setQuantity}
            setSelectedVariant={setSelectedVariant}
            stocksVariante={stocksVariante}
          />
        </div>
        <div>
          <QuantityField
            availableQuantities={availableQuantities}
            disableField={!!errorMsg}
            isValidReference={isValidReference}
            mediaMobile={true}
            quantity={quantity}
            reference={reference}
            setQuantity={setQuantity}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <TableRow className={styles.container}>
        <TableCell>
          <ReferenceField
            errorMsg={errorMsg}
            hasStock={!!stocks}
            isValidReference={isValidReference}
            mediaMobile={true}
            reference={reference}
            setReference={setReference}
          />
        </TableCell>
        <TableCell>
          <SizeField
            disableField={errorMsg == t('LABEL_PRODUCT_NOT_FOUND')}
            isValidReference={isValidReference}
            mediaMobile={true}
            reference={reference}
            setQuantity={setQuantity}
            setSelectedVariant={setSelectedVariant}
            stocksVariante={stocksVariante}
          />
        </TableCell>
        <TableCell>
          <QuantityField
            availableQuantities={availableQuantities}
            disableField={!!errorMsg}
            isValidReference={isValidReference}
            mediaMobile={true}
            quantity={quantity}
            reference={reference}
            setQuantity={setQuantity}
          />
        </TableCell>
        <TableCell>
          <button
            className={styles.deleteButton}
            type="button"
            onClick={handleDelete}
          >
            <img src={DeleteIcon} alt="remove" className={styles.deleteIcon} />
            <div className={styles.deleteLabel}>{t('LABEL_DELETE')}</div>
          </button>
        </TableCell>
      </TableRow>
    </>
  );
}

export default Reference;
